import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const euro = (value) =>
  Number(value || 0).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });

export default function AppAziendaNuovoOrdinePage() {
  const navigate = useNavigate();
  const [prodotti, setProdotti] = useState([]);
  const [quantita, setQuantita] = useState({});
  const [ricerca, setRicerca] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState("");

  useEffect(() => {
    let attivo = true;

    async function carica() {
      try {
        const response = await authFetch(
          "/api/prodotti/?scope=catalogo&tipo_listino=azienda"
        );
        if (!response.ok) throw new Error("Catalogo non disponibile");

        const data = await response.json();
        const elenco = Array.isArray(data)
          ? data
          : Array.isArray(data.results)
            ? data.results
            : [];

        if (attivo) setProdotti(elenco);
      } catch (error) {
        if (attivo) setErrore(error.message);
      } finally {
        if (attivo) setLoading(false);
      }
    }

    carica();
    return () => { attivo = false; };
  }, []);

  const prezzo = (p) => Number(
    p.prezzo ?? p.price_azienda ?? p.prezzo_azienda ?? 0
  );

  const filtrati = prodotti.filter((p) =>
    String(p.nome || p.name || "")
      .toLowerCase()
      .includes(ricerca.toLowerCase())
  );

  const righe = useMemo(
    () => prodotti
      .filter((p) => Number(quantita[p.id] || 0) > 0)
      .map((p) => ({
        product: p.id,
        quantity: Number(quantita[p.id]),
        prezzo: prezzo(p),
      })),
    [prodotti, quantita]
  );

  const totale = righe.reduce(
    (somma, r) => somma + r.prezzo * r.quantity, 0
  );

  function modifica(id, valore) {
    const numero = Number(valore);
    if (!Number.isInteger(numero) || numero < 0) return;
    setQuantita((precedente) => ({
      ...precedente,
      [id]: numero,
    }));
  }

  async function creaOrdine() {
    if (!righe.length || saving) return;
    setSaving(true);
    setErrore("");

    try {
      const response = await authFetch("/api/ordini/miei/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: "DRAFT",
          payment_method: "RITIRO",
          items: righe.map(({ product, quantity }) => ({
            product,
            quantity,
          })),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(JSON.stringify(data));
      }

      if (!data.id) throw new Error("ID ordine mancante");

      navigate(`/app/azienda/ordini/${data.id}/riepilogo`);
    } catch (error) {
      setErrore(error.message || "Errore nella creazione dell'ordine");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container py-4" style={{ maxWidth: 850 }}>
      <button
        className="btn btn-link px-0"
        onClick={() => navigate("/app")}
      >
        ← Area Azienda
      </button>

      <h2 className="fw-bold mb-2">Nuovo ordine</h2>
      <p className="text-muted">
        Seleziona i prodotti e le quantità per la tua attività.
      </p>

      <input
        className="form-control mb-4"
        placeholder="Cerca un prodotto..."
        value={ricerca}
        onChange={(e) => setRicerca(e.target.value)}
      />

      {errore && (
        <div className="alert alert-danger">{errore}</div>
      )}

      {loading && <p>Caricamento catalogo...</p>}

      {!loading && filtrati.map((p) => {
        const q = Number(quantita[p.id] || 0);
        return (
          <div className="card mb-2" key={p.id}>
            <div className="card-body d-flex align-items-center gap-3">
              <div className="flex-grow-1">
                <div className="fw-semibold">{p.nome || p.name}</div>
                <div className="text-muted">{euro(prezzo(p))}</div>
              </div>
              <button
                className="btn btn-outline-secondary"
                onClick={() => modifica(p.id, Math.max(0, q - 1))}
              >
                −
              </button>
              <span style={{ minWidth: 24, textAlign: "center" }}>
                {q}
              </span>
              <button
                className="btn btn-outline-secondary"
                onClick={() => modifica(p.id, q + 1)}
              >
                +
              </button>
            </div>
          </div>
        );
      })}

      <div className="card mt-4">
        <div className="card-body">
          <div className="d-flex justify-content-between mb-3">
            <strong>Prodotti selezionati: {righe.length}</strong>
            <strong>Totale: {euro(totale)}</strong>
          </div>
          <button
            className="btn btn-dark w-100 rounded-pill"
            disabled={saving || !righe.length}
            onClick={creaOrdine}
          >
            {saving ? "Creazione in corso..." : "Continua al riepilogo"}
          </button>
        </div>
      </div>
    </div>
  );
}
